/**
 * Cookie Consent Management Utility
 * 
 * Helper functions to manage and check cookie consent preferences
 */

const CONSENT_KEY = 'cookieConsent';

/**
 * Get the current cookie consent preferences
 * @returns {Object|null} The consent preferences or null if not set
 */
export const getConsent = () => {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    return consent ? JSON.parse(consent) : null;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

/**
 * Check if user has given consent for a specific cookie category
 * @param {string} category - The cookie category to check (necessary, analytics, marketing, preferences)
 * @returns {boolean} True if consent is given, false otherwise
 */
export const hasConsent = (category) => {
  const consent = getConsent();
  if (!consent) return false;
  
  // Necessary cookies are always allowed
  if (category === 'necessary') return true;
  
  return consent[category] === true;
};

/**
 * Save cookie consent preferences
 * @param {Object} preferences - The consent preferences object
 */
export const saveConsent = (preferences) => {
  try {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    
    // Dispatch custom event for other components to react to consent changes
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
      detail: consentData 
    }));
  } catch (error) {
    console.error('Error saving cookie consent:', error);
  }
};

/**
 * Clear cookie consent preferences
 */
export const clearConsent = () => {
  try {
    localStorage.removeItem(CONSENT_KEY);
    window.dispatchEvent(new CustomEvent('cookieConsentCleared'));
  } catch (error) {
    console.error('Error clearing cookie consent:', error);
  }
};

/**
 * Check if user has made a consent choice
 * @returns {boolean} True if consent choice exists, false otherwise
 */
export const hasConsentChoice = () => {
  return getConsent() !== null;
};

/**
 * Initialize analytics scripts if consent is given
 */
export const initializeAnalytics = () => {
  if (!hasConsent('analytics')) return;
  
  // Add your analytics initialization code here
  // Example: Google Analytics, Matomo, etc.
  console.log('Analytics initialized');
};

/**
 * Initialize marketing scripts if consent is given
 */
export const initializeMarketing = () => {
  if (!hasConsent('marketing')) return;
  
  // Add your marketing/advertising scripts here
  // Example: Google Ads, Facebook Pixel, etc.
  console.log('Marketing scripts initialized');
};

/**
 * Apply preference cookies if consent is given
 */
export const applyPreferences = () => {
  if (!hasConsent('preferences')) return;
  
  // Add preference-related functionality here
  console.log('Preferences applied');
};

/**
 * Initialize all scripts based on consent
 */
export const initializeScripts = () => {
  const consent = getConsent();
  if (!consent) return;
  
  if (consent.analytics) initializeAnalytics();
  if (consent.marketing) initializeMarketing();
  if (consent.preferences) applyPreferences();
};

// Listen for consent changes and initialize scripts accordingly
if (typeof window !== 'undefined') {
  window.addEventListener('cookieConsentChanged', (event) => {
    initializeScripts();
  });
}

